import React, { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Sparkles, Calendar } from "lucide-react";

/* ---------------- Posts data ---------------- */
const POSTS = {
  "how-to-write-a-brand-story": {
    slug: "how-to-write-a-brand-story",
    title: "How to Write a Brand Story That Actually Connects",
    excerpt:
      "A practical 5-step framework for crafting brand stories founders, marketers, and creators can use today — without writing a single product feature.",
    metaTitle:
      "How to Write a Brand Story (5-Step Framework) | The Story Assembly",
    metaDescription:
      "Learn how to write a brand story that connects with customers. A practical 5-step framework for founders, marketers and creators — with examples.",
    date: "January 2026",
    readTime: "6 min read",
    category: "Brand Storytelling",
    sections: [
      {
        type: "lede",
        body: [
          "Most brand stories fail for one reason: they're written from the inside out.",
          "Founders describe what they make. They list features. They explain their process.",
          "But customers don't buy what you make. They buy who they become when they use it.",
          "Here's a 5-step framework to flip that around — and write a story people actually remember.",
        ],
      },
      {
        type: "h2",
        text: "1. Start with the transformation, not the product",
      },
      {
        type: "p",
        body: [
          "Before you mention what you sell, answer this: what changes in your customer's life after they engage with your brand?",
          "Patagonia doesn't sell jackets — it sells a way of moving through the world that respects it. Notion doesn't sell software — it sells the calm of an organised mind.",
          "Write down the transformation in one sentence. That sentence is your story's spine.",
        ],
      },
      {
        type: "h2",
        text: "2. Name a specific tension",
      },
      {
        type: "p",
        body: [
          "Every memorable story has friction. Without tension, there's nothing for the reader to lean into.",
          "Ask: what does your customer struggle with that no one talks about? What contradiction does your industry pretend doesn't exist?",
          "Loomtide names the tension between sustainability and style. Northpen names the tension between writing and getting distracted by the page itself. Your tension is the door — find it and walk through.",
        ],
      },
      {
        type: "h2",
        text: "3. Write in your customer's voice, not your own",
      },
      {
        type: "p",
        body: [
          "Read your draft out loud. Does it sound like a person? Or a press release?",
          "Replace every \"we are committed to\" with a real verb. Replace every \"solutions\" with a real noun. Replace every \"empower\" with what you actually do.",
          "If your customer wouldn't say it to a friend, cut it.",
        ],
      },
      {
        type: "h2",
        text: "4. Earn the emotion, don't claim it",
      },
      {
        type: "p",
        body: [
          "\"We're passionate.\" \"We care.\" \"We believe.\"",
          "Every brand says this. None of it lands.",
          "Instead, show the choice you made because of that emotion. Did you reject easier suppliers for ethical ones? Did you give up a higher-margin product because it broke trust? Tell the small, expensive decision. That's what readers remember.",
        ],
      },
      {
        type: "h2",
        text: "5. End with what your customer becomes",
      },
      {
        type: "p",
        body: [
          "A brand story without a future state is just an autobiography.",
          "Close on the version of your customer that exists on the other side of your product. That image — clear, specific, attainable — is what makes them buy.",
          "Nike's customers don't end the story owning shoes. They end it knowing they can finish the run.",
        ],
      },
      {
        type: "pullquote",
        text: "A brand story is not what you say about yourself. It's the future your customer can see when they read it.",
      },
      {
        type: "h2",
        text: "A 60-second template",
      },
      {
        type: "p",
        body: [
          "Use this skeleton as a starting point. Fill in the brackets with your specifics:",
        ],
      },
      {
        type: "template",
        lines: [
          "[Brand] exists because [tension your customer feels].",
          "We believe [what your industry gets wrong].",
          "So we [the specific, expensive choice you made].",
          "For [your customer], that means [transformation].",
          "Because [who they get to become].",
        ],
      },
      {
        type: "h2",
        text: "What to do next",
      },
      {
        type: "p",
        body: [
          "If you have 60 seconds, paste a one-line description of your brand into The Story Assembly demo and let the generator produce a draft for you to refine. It's free for three generations per day, and the output is structured exactly like this framework — story, taglines, tone of voice, elevator pitch.",
          "If you have 60 minutes, run the framework above on your own brand. Get a friend who isn't paid by you to read it back. The story they paraphrase is your real brand story.",
        ],
      },
    ],
  },
  "tagline-writing-guide": {
    slug: "tagline-writing-guide",
    title: "The Tagline Writing Guide: 7 Rules for Lines That Last",
    excerpt:
      "Most taglines are forgotten the moment they're read. Here are seven rules — and the questions behind them — for writing taglines that stick.",
    metaTitle:
      "Tagline Writing Guide: 7 Rules for Memorable Taglines | The Story Assembly",
    metaDescription:
      "A practical guide to writing taglines that last. Seven rules with examples from Nike, Apple, Patagonia and more — by The Story Assembly.",
    date: "January 2026",
    readTime: "7 min read",
    category: "Tagline Writing",
    sections: [
      {
        type: "lede",
        body: [
          "A tagline is the smallest unit of brand strategy.",
          "Six words, sometimes three, sometimes one — and they have to do the work of an entire pitch deck.",
          "When taglines work, they outlive campaigns, founders, and even product lines. \"Just Do It.\" \"Think Different.\" \"The Ultimate Driving Machine.\"",
          "Here are seven rules — distilled from a decade of writing them — for taglines that don't fade.",
        ],
      },
      { type: "h2", text: "1. Write the verb, not the adjective" },
      {
        type: "p",
        body: [
          "Adjectives describe. Verbs do.",
          "\"Premium coffee, expertly roasted\" describes. \"Wake up the world\" acts. The second feels like a brand with somewhere to be.",
          "When you start drafting, list every verb your customer wishes they could do more of. Run, build, ship, sleep, breathe, finish. Build the tagline around one of those.",
        ],
      },
      { type: "h2", text: "2. Earn the obvious" },
      {
        type: "p",
        body: [
          "\"Just Do It\" sounds obvious now. In 1988, it wasn't — it was a permission slip to people who felt unqualified to start.",
          "A great tagline says something everyone already feels but no one has said in your category. The obviousness is earned by naming it first.",
          "Ask yourself: what does my customer wish a brand would say out loud?",
        ],
      },
      { type: "h2", text: "3. One tagline, one job" },
      {
        type: "p",
        body: [
          "Taglines fail when they try to do everything — describe the product, hint at the price, project the values, signal the audience.",
          "Pick one. Either it tells the customer what they get, what they become, or what you believe. Three different jobs, three different taglines. Don't blend.",
        ],
      },
      {
        type: "pullquote",
        text: "A tagline isn't a summary. It's a doorway — small, specific, and pointed at someone in particular.",
      },
      { type: "h2", text: "4. Cut every word twice" },
      {
        type: "p",
        body: [
          "Write your tagline. Then delete every word that isn't load-bearing. Then do it again.",
          "\"The fastest, easiest way to ship beautiful software\" → \"Ship beautiful software\" → \"Ship beautifully.\"",
          "Each cut reveals what the tagline was actually trying to say.",
        ],
      },
      { type: "h2", text: "5. Read it in three voices" },
      {
        type: "p",
        body: [
          "Say your tagline aloud as a skeptical investor, a tired customer, and a competitor.",
          "If the skeptic rolls their eyes, it's too vague. If the customer sighs, it's too tired. If the competitor could use the exact same line, it's too generic.",
          "If all three lean in, you've got something.",
        ],
      },
      { type: "h2", text: "6. Tag the feeling, not the feature" },
      {
        type: "p",
        body: [
          "Patagonia could have said \"Recycled materials, ethical supply chain.\" They said \"We're in business to save our home planet.\"",
          "Volvo could have said \"Triple-zone airbags and reinforced steel frame.\" They said \"For life.\"",
          "Features change. Feelings compound.",
        ],
      },
      { type: "h2", text: "7. If it could be your competitor's tagline, throw it out" },
      {
        type: "p",
        body: [
          "Replace your brand name with a competitor's in the tagline. Does it still work?",
          "If yes, it's not your tagline. It's the category's. Start again.",
          "A real tagline is a fingerprint, not a stamp.",
        ],
      },
      { type: "h2", text: "A 10-minute tagline drill" },
      {
        type: "p",
        body: [
          "Try this: take 10 minutes, no edits allowed. Write 30 lines. Don't judge them. Don't refine them.",
          "After 30, walk away for an hour. Come back and circle the three that sound like a person — not a brand.",
          "Now you have something to refine. Almost every great tagline started in a list of 27 bad ones.",
        ],
      },
      { type: "h2", text: "Want a head start?" },
      {
        type: "p",
        body: [
          "Paste a line about your brand into The Story Assembly demo. It returns three tagline directions in different registers — punchy, declarative, soulful — so you can find the voice that's actually yours before you start cutting.",
          "It's free for three generations a day. Use them as a draft, then sharpen by hand.",
        ],
      },
    ],
  },
  "brand-voice-vs-tone": {
    slug: "brand-voice-vs-tone",
    title: "Brand Voice vs Tone: The Difference That Changes Everything",
    excerpt:
      "Voice is who you are. Tone is how you sound in the room. Most brands confuse the two — and pay for it in inconsistency. Here's the practical difference.",
    metaTitle:
      "Brand Voice vs Tone: The Practical Difference | The Story Assembly",
    metaDescription:
      "Learn the practical difference between brand voice and tone, with examples, a definition framework and a one-page worksheet — by The Story Assembly.",
    date: "January 2026",
    readTime: "5 min read",
    category: "Brand Voice",
    sections: [
      {
        type: "lede",
        body: [
          "If your brand were a person at a dinner party, voice is who they are. Tone is how they sound when the conversation shifts.",
          "The same person can be playful with friends, serious in a meeting, and warm at a funeral — without changing who they are.",
          "Brands work the same way. Get the distinction wrong and your marketing reads like three different companies wrote it.",
        ],
      },
      { type: "h2", text: "Voice: the part that doesn't move" },
      {
        type: "p",
        body: [
          "Voice is your brand's personality. It's the words you reach for, the rhythm of your sentences, the things you'd never say.",
          "Voice is constant across every channel — a landing page, a refund email, a job listing, a tweet — they should all feel like the same hand wrote them.",
          "Define voice with traits, not adjectives. \"Warm but never saccharine.\" \"Direct, but explains the why.\" \"Witty, but never at the customer's expense.\"",
        ],
      },
      { type: "h2", text: "Tone: the part that shifts" },
      {
        type: "p",
        body: [
          "Tone is voice in context. The same brand will be celebratory on a launch announcement, apologetic on a service outage page, and reassuring inside a checkout flow.",
          "What changes is the dial — humour up or down, formality up or down, urgency up or down. What doesn't change is the underlying voice.",
          "Mailchimp's outage page and Mailchimp's confetti screen are obviously the same brand. That's tone doing its job.",
        ],
      },
      {
        type: "pullquote",
        text: "Voice tells the customer who you are. Tone tells them you understand what they need right now.",
      },
      { type: "h2", text: "The 3-trait voice framework" },
      {
        type: "p",
        body: [
          "Skip the 60-page brand bible. For 95% of brands, three traits are enough to write consistently.",
          "Pick three traits. For each, write what it IS and what it ISN'T. The \"isn't\" is doing most of the work.",
        ],
      },
      {
        type: "template",
        lines: [
          "Trait 1 — Direct. (Not blunt.)",
          "Trait 2 — Warm. (Not sentimental.)",
          "Trait 3 — Curious. (Not cute.)",
        ],
      },
      { type: "h2", text: "Map tone to four moments" },
      {
        type: "p",
        body: [
          "Define tone for four scenarios — your brand will encounter all of them within its first year:",
          "1. The launch (excited, confident, but not hyperbolic).",
          "2. The outage (honest, calm, specific — never \"we apologise for the inconvenience\").",
          "3. The onboarding (encouraging, clear, low-friction).",
          "4. The refund (gracious, respectful, no guilt-tripping).",
          "If your team can write all four in your brand voice without sounding inconsistent, you have a system.",
        ],
      },
      { type: "h2", text: "Test: the swap test" },
      {
        type: "p",
        body: [
          "Take any line of copy from your brand. Could a competitor's name go in the byline and the line would still work? If yes, your voice isn't doing its job.",
          "Now take a customer support reply, a homepage headline, and a tweet. Read all three out loud back-to-back. Do they sound like the same person? If not, your tone is overriding your voice instead of expressing it.",
        ],
      },
      { type: "h2", text: "Build it in 30 minutes" },
      {
        type: "p",
        body: [
          "Generate a quick voice sketch using The Story Assembly's demo. Paste one line about your brand and the output will give you a tone-of-voice paragraph alongside the brand story.",
          "Take the three strongest descriptors, turn them into IS/ISN'T pairs, and you've got the first draft of a voice guide that fits on one page.",
        ],
      },
    ],
  },
  "positioning-vs-branding": {
    slug: "positioning-vs-branding",
    title: "Positioning vs Branding: The Difference Founders Get Wrong",
    excerpt:
      "Positioning is the decision. Branding is the dramatisation. Founders who confuse them spend months on logos and never figure out who they're for.",
    metaTitle:
      "Positioning vs Branding: The Practical Difference | The Story Assembly",
    metaDescription:
      "Understand the difference between positioning and branding — and why founders who skip positioning end up with a beautiful brand for no one. A practical guide.",
    date: "January 2026",
    readTime: "6 min read",
    category: "Positioning",
    sections: [
      {
        type: "lede",
        body: [
          "Most founders skip positioning and jump straight to branding.",
          "They pick a name, hire a designer, and ship a logo before they've decided who the brand is for or what it's against.",
          "Six months later, they have a beautiful brand for no one — and they can't figure out why nothing converts.",
          "Here's the practical difference, and how to get them in the right order.",
        ],
      },
      { type: "h2", text: "Positioning is the decision" },
      {
        type: "p",
        body: [
          "Positioning is the strategic choice underneath the brand. It's three questions, answered with conviction:",
          "1. Who is this for? (And — critically — who is this NOT for?)",
          "2. What category are we in? (Or are we creating a new one?)",
          "3. What do we believe that our competitors don't?",
          "Until those answers exist, every branding decision is a guess.",
        ],
      },
      { type: "h2", text: "Branding is the dramatisation" },
      {
        type: "p",
        body: [
          "Branding is how positioning becomes felt. It's the logo, the voice, the colour palette, the typography, the photography style, the way the receipt is worded.",
          "Branding is what makes positioning visible — but it can't invent positioning that isn't there. A great logo for a brand with no positioning is just a nice-looking logo.",
        ],
      },
      {
        type: "pullquote",
        text: "Positioning is the bet. Branding is how you make the bet legible to the people you placed it on.",
      },
      { type: "h2", text: "The test: can you finish these sentences?" },
      {
        type: "p",
        body: [
          "If you can finish all four out loud — without hedging — you have positioning. If you can't, what you have is branding without a backbone.",
        ],
      },
      {
        type: "template",
        lines: [
          "We exist for [a specific person], not [the obvious adjacent person].",
          "We're in [category], but we reject [the category's default assumption].",
          "We believe [a strong opinion most competitors won't say out loud].",
          "If a customer doesn't [care about X], they're not for us.",
        ],
      },
      { type: "h2", text: "The order that actually works" },
      {
        type: "p",
        body: [
          "1. Positioning first — write it down in one paragraph. Get a friend who isn't paid by you to read it back. The version they paraphrase is your real positioning.",
          "2. Voice and story second — derive them from positioning. The voice is how the bet sounds. The story is why the bet exists.",
          "3. Visual identity last — colour, type and logo are the easiest to redo and the most expensive to do twice. Decide them last, when everything above is settled.",
          "Founders who do this in reverse spend twice as long and convert half as well.",
        ],
      },
      { type: "h2", text: "Worked example" },
      {
        type: "p",
        body: [
          "Patagonia's positioning: \"For people who'd rather repair than replace, in a category that profits from replacement.\"",
          "From that single sentence, you can derive the voice (direct, slightly inconvenient, ethically loud), the story (a company that sometimes tells you NOT to buy), and the visual identity (utilitarian, mountain-coloured, no glamour).",
          "Try doing it in reverse — start with the visual identity. You'd land somewhere generic, because the visual can't tell you what the brand is for.",
        ],
      },
      { type: "h2", text: "Use the generator as a positioning draft" },
      {
        type: "p",
        body: [
          "Drop one line about your brand into The Story Assembly demo. The output gives you a story, a tone of voice and a brand-line — all of which exposes whether your positioning is sharp or generic.",
          "If the output reads like it could belong to any of your competitors, the problem isn't the AI. It's that your positioning hasn't been decided yet.",
        ],
      },
    ],
  },
  "when-your-brand-sounds-generic": {
    slug: "when-your-brand-sounds-generic",
    title: "When Your Brand Sounds Generic: A 4-Step Rescue Plan",
    excerpt:
      "If your homepage could belong to three of your competitors, you're not building a brand — you're contributing to a category. Here's how to get specific again.",
    metaTitle:
      "When Your Brand Sounds Generic: A 4-Step Rescue Plan | The Story Assembly",
    metaDescription:
      "If your brand copy could belong to any of your competitors, you have a specificity problem. A 4-step rescue plan to sound like a real company again.",
    date: "January 2026",
    readTime: "5 min read",
    category: "Brand Voice",
    sections: [
      {
        type: "lede",
        body: [
          "Run this test on your homepage right now.",
          "Cover your logo. Replace your brand name with three competitors, one at a time. Does the copy still work?",
          "If yes, congratulations — you've spent months building a brand that sounds like the category, not like you.",
          "Here are four moves to get specific again.",
        ],
      },
      { type: "h2", text: "1. Replace abstract nouns with named choices" },
      {
        type: "p",
        body: [
          "Every generic homepage runs on the same five words: solutions, empower, innovative, seamless, premium.",
          "These are abstract nouns. They describe nothing. They're the verbal equivalent of stock photography.",
          "Replace each one with the specific choice underneath it. \"Premium ingredients\" → \"Sourced from one farm in Coorg.\" \"Seamless onboarding\" → \"Three screens. No demo call.\"",
          "Specificity isn't a tactic. It's the entire point.",
        ],
      },
      { type: "h2", text: "2. Name your enemy" },
      {
        type: "p",
        body: [
          "Generic brands try to be liked by everyone. Memorable brands name what they're against.",
          "Apple was against the beige PC. Liquid Death is against soulless water marketing. Notion is against the chaos of 14 SaaS tabs.",
          "Ask: what does our industry pretend is normal that we think is broken? Say that on the homepage. The right people will lean in. The wrong people will leave. Both are correct.",
        ],
      },
      {
        type: "pullquote",
        text: "Generic brands try to be acceptable to many. Specific brands are intolerable to some — and unforgettable to the rest.",
      },
      { type: "h2", text: "3. Tell one expensive truth" },
      {
        type: "p",
        body: [
          "Every brand has one truth they're afraid to put on the homepage — usually because it'll cost them customers.",
          "Patagonia's was \"Don't Buy This Jacket.\" Basecamp's was \"It will not scale.\" Yours might be: \"We're slower than them.\" Or: \"You can't use this on weekends.\" Or: \"This is not for everyone.\"",
          "Whatever it is, putting it where customers can read it is the single fastest way to stop sounding generic. It signals you have skin in the game.",
        ],
      },
      { type: "h2", text: "4. Borrow nothing from your category" },
      {
        type: "p",
        body: [
          "Look at the three biggest brands in your category. Note their colour palette, layout, typography, and the words they reach for.",
          "Now do none of it.",
          "If everyone in your space uses navy, gold and serif headlines (waves at us), use something else — or use the same palette with a totally different rhythm. Brand differentiation comes from the visual + verbal departure from the obvious.",
          "The fastest founders aren't the ones who copy the category leader. They're the ones who studied the leader, then chose the opposite on purpose.",
        ],
      },
      { type: "h2", text: "A 30-minute homework block" },
      {
        type: "p",
        body: [
          "1. Open your homepage. Highlight every abstract noun. Replace each with a specific choice. (10 min)",
          "2. Write one sentence beginning with \"We're against ___.\" Paste it somewhere visible. (5 min)",
          "3. Add one expensive truth to your About or Pricing page. (10 min)",
          "4. Show all three changes to a friend. If they say \"oh, interesting\" you're on track. If they say \"that's nice\" you haven't gone far enough. (5 min)",
        ],
      },
      { type: "h2", text: "What our generator does next" },
      {
        type: "p",
        body: [
          "Drop your one-line brand description into The Story Assembly demo. The output specifically avoids the five generic words — and forces you to articulate the tension, the audience and the bet underneath your idea.",
          "If you find yourself thinking \"that's not quite right\" while reading it, that's the point. It's a mirror, not an oracle. Use it to find your edges, then sharpen them by hand.",
        ],
      },
    ],
  },
};

export const POST_SLUGS = Object.keys(POSTS);

const Kicker = ({ children, className = "" }) => (
  <div
    className={`font-mono-display text-[11px] uppercase tracking-[0.22em] text-[#6B655B] ${className}`}
  >
    {children}
  </div>
);

const SharedHeader = () => (
  <header className="border-b border-[#DCD7C9]">
    <div className="max-w-3xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
      <Link
        to="/"
        data-testid="blog-back"
        className="inline-flex items-center gap-2 text-sm text-[#0A1E3F] hover:text-[#D4A53A] transition-colors"
      >
        <ArrowLeft size={14} /> The Story Assembly
      </Link>
      <div className="flex items-center gap-2">
        <img
          src="https://customer-assets.emergentagent.com/job_tale-builder-110/artifacts/rxbavk25_ChatGPT%20Image%20May%2029%2C%202025%2C%2010_23_27%20PM.png"
          alt="The Story Assembly"
          className="h-7 w-7 rounded-md object-cover"
        />
        <span className="font-serif text-base tracking-tight hidden sm:inline">
          The <span className="italic font-light">Story</span>{" "}
          <span className="text-[#D4A53A]">Assembly</span>
        </span>
      </div>
    </div>
  </header>
);

const setMeta = (title, description) => {
  document.title = title;
  let meta = document.querySelector('meta[name="description"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "description");
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", description);
};

/* ---------------- Blog Index ---------------- */
export const BlogIndex = () => {
  useEffect(() => {
    const prev = [document.title, document.querySelector('meta[name="description"]')?.getAttribute("content")];
    setMeta(
      "Brand Storytelling Journal | The Story Assembly",
      "Practical, founder-tested guides on brand storytelling, positioning and tagline writing — by The Story Assembly."
    );
    return () => setMeta(prev[0], prev[1] || "");
  }, []);

  const posts = Object.values(POSTS);

  return (
    <div className="min-h-screen bg-[#F4F1EA] text-[#1C1A17]">
      <SharedHeader />
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <Kicker>The Journal</Kicker>
        <h1 className="font-serif text-5xl md:text-7xl tracking-tighter mt-3 text-[#0A1E3F] leading-[1.02]">
          Brand storytelling, written down.
        </h1>
        <p className="mt-6 text-[17px] leading-relaxed text-[#1C1A17]/75 max-w-2xl">
          Field-tested essays on positioning, voice, and how founders earn
          attention with words that sound like a person — not a press release.
        </p>

        <div className="mt-14 space-y-8">
          {posts.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="block border-t border-[#DCD7C9] pt-8 group"
            >
              <div className="flex items-center gap-4 text-[11px] font-mono-display uppercase tracking-[0.18em] text-[#6B655B]">
                <span>{p.category}</span>
                <span>·</span>
                <span>{p.readTime}</span>
                <span>·</span>
                <span>{p.date}</span>
              </div>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tighter mt-3 text-[#0A1E3F] leading-tight group-hover:text-[#D4A53A] transition-colors">
                {p.title}
              </h2>
              <p className="mt-3 text-[16px] leading-relaxed text-[#1C1A17]/75">
                {p.excerpt}
              </p>
              <span className="inline-flex items-center gap-1.5 mt-4 text-[12px] font-mono-display uppercase tracking-[0.18em] text-[#0A1E3F] group-hover:text-[#D4A53A] transition-colors">
                Read essay <ArrowRight size={12} />
              </span>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
};

/* ---------------- Single Post ---------------- */
const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = POSTS[slug];

  useEffect(() => {
    if (!post) return;
    const prev = [document.title, document.querySelector('meta[name="description"]')?.getAttribute("content")];
    setMeta(post.metaTitle, post.metaDescription);
    return () => setMeta(prev[0], prev[1] || "");
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#F4F1EA] flex items-center justify-center px-6">
        <div className="text-center">
          <Kicker>404</Kicker>
          <h1 className="font-serif text-5xl tracking-tighter mt-2 text-[#0A1E3F]">
            Essay not found.
          </h1>
          <button
            onClick={() => navigate("/blog")}
            className="mt-6 inline-flex items-center gap-2 rounded-full px-5 py-3 bg-[#0A1E3F] text-[#F4F1EA] text-sm"
          >
            <ArrowLeft size={14} /> Back to the journal
          </button>
        </div>
      </div>
    );
  }

  const renderBlock = (b, i) => {
    if (b.type === "lede") {
      return (
        <div key={i} className="space-y-5 text-[19px] leading-[1.7] text-[#0A1E3F] font-serif">
          {b.body.map((p) => (
            <p key={p}>{p}</p>
          ))}
        </div>
      );
    }
    if (b.type === "h2") {
      return (
        <h2
          key={i}
          className="font-serif text-3xl md:text-4xl tracking-tighter text-[#0A1E3F] leading-tight mt-14 mb-5"
        >
          {b.text}
        </h2>
      );
    }
    if (b.type === "p") {
      return (
        <div key={i} className="space-y-4 text-[17px] leading-[1.75] text-[#1C1A17]/85">
          {b.body.map((p) => (
            <p key={p}>{p}</p>
          ))}
        </div>
      );
    }
    if (b.type === "pullquote") {
      return (
        <blockquote
          key={i}
          className="my-14 border-l-2 border-[#D4A53A] pl-6 font-serif text-2xl md:text-3xl italic text-[#0A1E3F] leading-snug"
        >
          {b.text}
        </blockquote>
      );
    }
    if (b.type === "template") {
      return (
        <div
          key={i}
          className="my-8 border border-[#0A1E3F]/15 rounded-md bg-[#0A1E3F] text-[#F4F1EA] p-6 md:p-8 font-serif text-[18px] leading-[1.7] space-y-3"
        >
          {b.lines.map((l) => (
            <p key={l}>{l}</p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#F4F1EA] text-[#1C1A17]">
      <SharedHeader />
      <main className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-[11px] font-mono-display uppercase tracking-[0.22em] text-[#6B655B] hover:text-[#0A1E3F]"
        >
          <ArrowLeft size={12} /> The Journal
        </Link>

        <div className="mt-5 flex items-center gap-4 text-[11px] font-mono-display uppercase tracking-[0.18em] text-[#6B655B]">
          <span>{post.category}</span>
          <span>·</span>
          <span>{post.readTime}</span>
          <span>·</span>
          <span className="inline-flex items-center gap-1.5">
            <Calendar size={11} /> {post.date}
          </span>
        </div>
        <h1
          data-testid="post-title"
          className="font-serif text-5xl md:text-7xl tracking-tighter mt-4 leading-[1.02] text-[#0A1E3F]"
        >
          {post.title}
        </h1>

        <article className="mt-12">
          {post.sections.map(renderBlock)}
        </article>

        {/* Related essays */}
        {(() => {
          const others = Object.values(POSTS).filter((p) => p.slug !== post.slug).slice(0, 2);
          if (others.length === 0) return null;
          return (
            <section data-testid="related-essays" className="mt-20 pt-12 border-t border-[#DCD7C9]">
              <Kicker>Keep reading</Kicker>
              <div className="mt-6 grid sm:grid-cols-2 gap-6">
                {others.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/blog/${p.slug}`}
                    data-testid={`related-${p.slug}`}
                    className="group block border border-[#DCD7C9] rounded-md p-5 hover:border-[#D4A53A] transition-colors"
                  >
                    <div className="font-mono-display text-[10px] uppercase tracking-[0.18em] text-[#6B655B]">
                      {p.category} · {p.readTime}
                    </div>
                    <div className="mt-2 font-serif text-[22px] tracking-tight text-[#0A1E3F] leading-snug group-hover:text-[#D4A53A] transition-colors">
                      {p.title}
                    </div>
                    <div className="mt-3 text-[14px] leading-relaxed text-[#1C1A17]/70 line-clamp-3">
                      {p.excerpt}
                    </div>
                    <span className="inline-flex items-center gap-1.5 mt-4 text-[11px] font-mono-display uppercase tracking-[0.18em] text-[#0A1E3F] group-hover:text-[#D4A53A] transition-colors">
                      Read essay <ArrowRight size={11} />
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          );
        })()}

        {/* CTA */}
        <section className="mt-20 text-center border-t border-[#DCD7C9] pt-14">
          <Kicker>Try it</Kicker>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tighter mt-3 text-[#0A1E3F] leading-tight">
            Run the framework on your brand.
          </h2>
          <p className="mt-5 text-[16px] text-[#1C1A17]/75 max-w-lg mx-auto">
            Paste one line about your brand into our generator and get a story,
            three taglines, and a tone of voice in under 60 seconds.
          </p>
          <Link to="/#demo" className="inline-block mt-7">
            <button
              data-testid="post-cta"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 bg-[#D4A53A] text-[#0A1E3F] text-sm font-medium hover:bg-[#B58A26] transition-all active:scale-[0.97]"
            >
              <Sparkles size={15} /> Generate your brand story
              <ArrowRight size={14} />
            </button>
          </Link>
        </section>
      </main>
    </div>
  );
};

export default BlogPost;
